import { capitalize } from 'lodash';
import type { ProductTemplateAPIData } from 'types/catalog_types';

type ShufflerProductOrientation = 'vertical' | 'horizontal' | 'square';

type ShufflerProductSize = {
  orientation: ShufflerProductOrientation;
  width?: number;
  height?: number;
};

class ShufflerProduct {
  id: number;
  uid: string;
  subcatalog: string;
  name: string;
  nameHtml: string;
  slug: string;
  filterGroup: string;
  imageUrl: string;
  numphotos: number;
  numtext: number;
  format?: string;
  relatedProducts?: string[];

  private productSize: ShufflerProductSize;

  constructor(subcatalog: string, productTemplate: ProductTemplateAPIData) {
    this.id = productTemplate.id;
    this.uid = productTemplate.uid;
    this.subcatalog = subcatalog;
    this.name = productTemplate.name;
    this.nameHtml = productTemplate.name_long;
    this.slug = productTemplate.slug;
    this.filterGroup = productTemplate.filter_group;
    this.imageUrl = productTemplate.image_url;
    this.relatedProducts = productTemplate.custom_options?.related?.split(',');

    this.productSize = makeSize(productTemplate.uid);

    const { nodes } = productTemplate;
    this.numtext = nodes.filter(({ node_type: type }) => type === 3).length;
    this.numphotos = nodes.filter(({ node_type: type }) => type === 1).length;
  }

  get size() {
    const { height, width } = this.productSize;
    return height && width ? `${Math.min(width, height)}x${Math.max(width, height)}` : '';
  }

  get orientation(): Capitalize<ShufflerProductOrientation> {
    const { height, width, orientation } = this.productSize;
    const o = (!!width && !!height && makeOrientation(width, height)) || orientation;
    return capitalize(o) as Capitalize<ShufflerProductOrientation>;
  }

  get metaData() {
    return { numphotos: this.numphotos, numtext: this.numtext, hascustomtext: this.numtext > 0 };
  }

  get hascustomtext() {
    return this.numtext > 0;
  }
}

function makeSize(u: string) {
  const ret: ShufflerProductSize = { orientation: 'vertical' };
  if (u.match(/_H$/) || u.match(/[h]{1}(-(\d){2})*(_PopOut-[A-Za-z]+)*(_22)*$/)) {
    ret.orientation = 'horizontal';
  } else if (u.match(/_V$/) || u.match(/[v]{1}(-(\d){2})*(_22)*$/)) {
    ret.orientation = 'vertical';
  }

  if (u.indexOf('57') > -1) {
    ret.width = ret.orientation === 'vertical' ? 5 : 7;
    ret.height = ret.orientation === 'vertical' ? 7 : 5;
  } else if (u.indexOf('455') > -1) {
    ret.width = ret.orientation === 'vertical' ? 4 : 5.5;
    ret.height = ret.orientation === 'vertical' ? 5.5 : 4;
  } else if (u.indexOf('48') > -1) {
    ret.width = ret.orientation === 'vertical' ? 4 : 8;
    ret.height = ret.orientation === 'vertical' ? 8 : 4;
  } else if (u.indexOf('55') > -1) {
    ret.orientation = 'square';
    ret.width = 5;
    ret.height = 5;
  }

  return ret;
}

function makeOrientation(x: number, y: number): false | ShufflerProductOrientation {
  return (x > y && 'horizontal') || (y > x && 'vertical') || (y === x && 'square');
}

export function propInShufflerProduct(prop: string): prop is keyof ShufflerProduct {
  return prop in ShufflerProduct;
}

export default ShufflerProduct;
